import { useState } from 'react';
import { FormControl, Input } from '@airbus/components-react';
import { useLanguageContextAPI } from '../../context/LanguageContext';

type BulkUpdateNumberInputProps = {
  id: string;
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  min?: number;
  max?: number;
};

export default function BulkUpdateNumberInput({
  id,
  value,
  onChange,
  onBlur,
  disabled = false,
  hasError = false,
  errorMessage,
  min,
  max,
}: BulkUpdateNumberInputProps) {
  const { t } = useLanguageContextAPI();
  const [typeError, setTypeError] = useState<string | null>(null);
  const mergedError = typeError ?? errorMessage;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    if (nextValue === '') {
      setTypeError(null);
      onChange(null);
      return;
    }

    if (!/^-?\d*(\.\d*)?$/.test(nextValue)) {
      setTypeError('Only numbers are allowed');
      return;
    }

    const parsed = Number(nextValue);
    if (typeof min === 'number' && parsed < min) {
      setTypeError(t('minimum_limit', { min }));
      onChange(null);
      return;
    }

    if (typeof max === 'number' && parsed > max) {
      setTypeError(t('maximun_limit', { max }));
      onChange(null);
      return;
    }

    setTypeError(null);
    onChange(parsed);
  };

  return (
    <FormControl
      id={id}
      disabled={disabled}
      error={hasError || Boolean(mergedError)}
      helpText={mergedError || undefined}
      errorText={mergedError || undefined}
      className='input-small disabled-input'
    >
      <Input
        name={id}
        size='small'
        value={value ?? ''}
        readOnly={disabled}
        onChange={handleInputChange}
        onBlur={onBlur}
      />
    </FormControl>
  );
}
